import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MoreHorizontal, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareStrip — 3-dot expanding share menu.
 * Lives inline next to any modal's close X button.
 *
 * Interaction:
 *   - Idle: single circular 3-dot toggle.
 *   - Click 3-dot → 2 circular icon buttons slide out as one clean row
 *     from the right: Copy · Share.
 *   - Click one → briefly flashes brand color, fires action, collapses back.
 *   - Escape or outside click collapses without action.
 */
const ShareStrip = ({
  href,
  title = "SB Media Studio",
  text = "SB Media Studio — premium podcast, video, and UGC production in Lakeland, FL.",
  campaign = "share_modal",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [flash, setFlash] = useState(null); // 'share' | 'copy'
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const buildUrl = () => {
    const base =
      href ||
      (typeof window !== "undefined"
        ? window.location.origin + window.location.pathname
        : "https://sbmediastudio.com/");
    const u = new URL(
      base,
      typeof window !== "undefined" ? window.location.origin : "https://sbmediastudio.com/",
    );
    u.searchParams.set("utm_source", "referral");
    u.searchParams.set("utm_medium", "share_modal");
    u.searchParams.set("utm_campaign", campaign);
    return u.toString();
  };

  const flashThen = (name, fn) => async () => {
    setFlash(name);
    setTimeout(() => setFlash(null), 900);
    try {
      await fn();
    } finally {
      setTimeout(() => setOpen(false), 700);
    }
  };

  const doShare = flashThen("share", async () => {
    const url = buildUrl();
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch (err) {
        if (err?.name !== "AbortError") toast.error("Couldn't open the share sheet.");
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share it with anyone.");
    }
  });

  const doCopy = flashThen("copy", async () => {
    try {
      await navigator.clipboard.writeText(buildUrl());
      setCopied(true);
      toast.success("Link copied — share it with anyone.");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy — long-press the link instead.");
    }
  });

  // Circle button — brand cyan flashes on click.
  const circleClass = (name) =>
    `w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${
      flash === name
        ? "bg-[#00f0ff] border-[#00f0ff] text-[#05050a]"
        : "bg-white/[0.05] border-white/15 text-white/85 hover:bg-white/[0.12]"
    }`;

  const actions = [
    { key: "copy", onClick: doCopy, Icon: copied ? Check : Copy, label: "Copy link" },
    { key: "share", onClick: doShare, Icon: Share2, label: "Share" },
  ];

  return (
    <div
      ref={wrapRef}
      className={`relative inline-flex items-center ${className}`}
      data-testid={`share-strip-${campaign}`}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            key="tray"
            className="absolute right-11 top-1/2 -translate-y-1/2 flex flex-row items-center gap-2"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={a.onClick}
                data-testid={`${a.key}-btn-${campaign}`}
                aria-label={a.label}
                title={a.label}
                className={circleClass(a.key)}
              >
                <a.Icon size={14} strokeWidth={2.2} />
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Share this"
        aria-expanded={open}
        data-testid={`share-toggle-${campaign}`}
        className="w-10 h-10 rounded-full bg-white/[0.05] border border-white/15 text-white/85 hover:bg-white/[0.12] transition-colors flex items-center justify-center"
        title="Share this"
      >
        <MoreHorizontal size={16} strokeWidth={2.2} />
      </button>
    </div>
  );
};

export default ShareStrip;
